import React, { useState, useEffect, useCallback, useRef } from 'react';
import Graph from './Graph';
import { hopTracerService, investigationService } from '../services/api';
import '../styles/HopTracerPanel.css';

// Hop depth colours — must match hop_tracer.py HOP_COLORS
const HOP_COLORS = {
  0: '#00d9ff',
  1: '#f59e0b',
  2: '#a855f7',
  3: '#ef4444',
  4: '#10b981',
};
const hopColor = (depth) => HOP_COLORS[Math.abs(depth)] ?? '#6b7280';

const TERMINAL_LABELS = {
  known_address:  { label: 'Known address (exchange/mixer/etc)', color: '#f59e0b' },
  max_depth:      { label: 'Max hop depth reached',              color: '#ef4444' },
  parked:         { label: 'Funds parked (no outbound)',         color: '#6b7280' },
  max_nodes:      { label: 'Node cap reached',                   color: '#a855f7' },
  window_expired: { label: 'Window expired',                     color: '#64748b' },
};

const PATTERN_META = {
  fan_out:  { icon: '🌐', label: 'Fan-out',    color: '#ef4444' },
  fan_in:   { icon: '🎯', label: 'Fan-in',     color: '#f59e0b' },
  round_num:{ icon: '🔢', label: 'Structuring', color: '#a855f7' },
  rapid:    { icon: '⚡', label: 'Rapid',       color: '#f59e0b' },
};

const SEVERITY_COLOR = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };

function fmtAddr(addr) {
  if (!addr) return '—';
  return `${addr.slice(0, 10)}...${addr.slice(-6)}`;
}

function fmtDate(isoStr) {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
    }) + ' UTC';
  } catch { return isoStr; }
}

function fmtVal(v) {
  const n = parseFloat(v) || 0;
  return n === 0 ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

// ── Progress bar ──────────────────────────────────────────────────────────────
function ProgressBar({ pct, message, nodes, depth }) {
  return (
    <div className="ht-progress-wrap">
      <div className="ht-progress-header">
        <span className="ht-progress-msg">{message || 'Running…'}</span>
        <span className="ht-progress-meta">{nodes} nodes · depth {depth} · {pct}%</span>
      </div>
      <div className="ht-progress-track">
        <div className="ht-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Pattern alert cards ───────────────────────────────────────────────────────
function PatternAlerts({ patterns }) {
  if (!patterns?.length) return null;
  return (
    <div className="ht-patterns-card">
      <h3>⚠️ Patterns Detected ({patterns.length})</h3>
      <div className="ht-patterns-list">
        {patterns.map((p, i) => {
          const meta = PATTERN_META[p.type] || { icon: '❓', label: p.type, color: '#6b7280' };
          return (
            <div key={i} className="ht-pattern-item" style={{ borderLeftColor: SEVERITY_COLOR[p.severity] || '#6b7280' }}>
              <div className="ht-pattern-top">
                <span className="ht-pattern-icon">{meta.icon}</span>
                <span className="ht-pattern-type" style={{ color: meta.color }}>{meta.label}</span>
                <span className="ht-pattern-severity" style={{ color: SEVERITY_COLOR[p.severity] }}>
                  {p.severity}
                </span>
                <span className="ht-addr" title={p.address}>{fmtAddr(p.address)}</span>
                {p.label && p.label !== fmtAddr(p.address) && (
                  <span className="ht-known-badge">{p.label}</span>
                )}
              </div>
              <div className="ht-pattern-detail">{p.detail}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Value flow panel ──────────────────────────────────────────────────────────
function ValueFlowPanel({ nodes }) {
  if (!nodes?.length) return null;

  // Top wallets by received value, excluding scammer (hop 0) and known addresses
  const ranked = [...nodes]
    .filter(n => n.hop_depth !== 0 && !n.known_info && parseFloat(n.received_value) > 0)
    .sort((a, b) => parseFloat(b.received_value) - parseFloat(a.received_value))
    .slice(0, 8);

  if (!ranked.length) return null;
  const maxVal = parseFloat(ranked[0].received_value) || 1;

  return (
    <div className="ht-valueflow-card">
      <h3>💰 Value Flow — Top Intermediate Wallets</h3>
      <div className="ht-valueflow-list">
        {ranked.map((n, i) => {
          const recv = parseFloat(n.received_value) || 0;
          const sent = parseFloat(n.sent_value)     || 0;
          const ret  = parseFloat(n.retained_value) || 0;
          const pct  = Math.round((recv / maxVal) * 100);
          return (
            <div key={i} className="ht-vf-row">
              <div className="ht-vf-left">
                <span className="ht-vf-depth" style={{ background: hopColor(n.hop_depth) }}>
                  {n.hop_depth > 0 ? `H${n.hop_depth}` : `I${Math.abs(n.hop_depth)}`}
                </span>
                <span className="ht-addr" title={n.address}>{fmtAddr(n.address)}</span>
                {n.pattern_flags?.length > 0 && (
                  <span className="ht-pattern-flags">
                    {n.pattern_flags.map(f => PATTERN_META[f]?.icon || '❓').join('')}
                  </span>
                )}
              </div>
              <div className="ht-vf-bar-wrap">
                <div className="ht-vf-bar" style={{ width: `${pct}%` }} />
              </div>
              <div className="ht-vf-values">
                <span title="Received">↓ {fmtVal(recv)}</span>
                <span title="Sent" style={{ color: '#94a3b8' }}>↑ {fmtVal(sent)}</span>
                {ret > 0 && <span title="Retained" style={{ color: '#f59e0b' }}>🔒 {fmtVal(ret)}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Main panel ────────────────────────────────────────────────────────────────
export default function HopTracerPanel({ investigation, focusAddress = null, onFocusAddressConsumed }) {
  const invId = investigation?.id;

  // Controls
  const [mode,             setMode]             = useState('outbound');
  const [maxHops,          setMaxHops]          = useState(4);
  const [dayBuffer,        setDayBuffer]        = useState(3);
  const [maxNodes,         setMaxNodes]         = useState(100);
  const [fanOutThreshold,  setFanOutThreshold]  = useState(10);

  // Optional override trace root, pre-filled when arriving from a node's
  // "Run Hop Tracer from here" button. Cleared once the user clears it or
  // a different node sends a new one.
  const [focusOverride, setFocusOverride] = useState(focusAddress);
  useEffect(() => {
    if (focusAddress) setFocusOverride(focusAddress);
  }, [focusAddress]);

  // State
  const [jobStatus,  setJobStatus]  = useState(null);   // null | status object
  const [result,     setResult]     = useState(null);
  const [seedData,   setSeedData]   = useState(null);
  const [error,      setError]      = useState(null);
  const [activeTab,  setActiveTab]  = useState('graph'); // graph | table | patterns | flow

  // Local hidden-state for the hop-trace graph (not persisted — resets on each new trace)
  const [htHiddenEdges, setHtHiddenEdges] = useState(new Set());
  const [htHiddenNodes, setHtHiddenNodes] = useState(new Set());
  const handleHtViewStateChange = useCallback((newEdges, newNodes) => {
    setHtHiddenEdges(new Set(newEdges));
    setHtHiddenNodes(new Set(newNodes));
  }, []);

  // Reset hidden state whenever a new trace result arrives
  useEffect(() => {
    setHtHiddenEdges(new Set());
    setHtHiddenNodes(new Set());
  }, [result]);

  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  // ── Polling ─────────────────────────────────────────────────────────────────
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      if (!invId) return;
      try {
        const { data } = await hopTracerService.getV2Status(invId);
        setJobStatus(data);
        if (data.status === 'complete') {
          stopPolling();
          const r = await hopTracerService.getV2Result(invId);
          setResult(r.data);
        } else if (['failed', 'cancelled'].includes(data.status)) {
          stopPolling();
          if (data.status === 'failed') setError(`Trace failed: ${data.message || 'Unknown error'}`);
        }
      } catch (e) {
        console.error('Poll error:', e);
      }
    }, 2000);
  }, [invId, stopPolling]);

  // ── Load existing result + seed data on mount ───────────────────────────────
  const loadExisting = useCallback(async () => {
    if (!invId) return;
    try {
      const [htRes, seedRes, statusRes] = await Promise.all([
        hopTracerService.getV2Result(invId).catch(() => ({ data: null })),
        investigationService.getTxLookupGraph(invId).catch(() => ({ data: null })),
        hopTracerService.getV2Status(invId).catch(() => ({ data: null })),
      ]);
      if (htRes.data?.graph?.nodes?.length)   setResult(htRes.data);
      if (seedRes.data?.transactions?.length) setSeedData(seedRes.data);
      if (statusRes.data?.status && statusRes.data.status !== 'none') {
        setJobStatus(statusRes.data);
        if (['queued', 'running'].includes(statusRes.data.status)) startPolling();
      }
    } catch (e) {
      console.error('HopTracerPanel load error:', e);
    }
  }, [invId, startPolling]);

  useEffect(() => { loadExisting(); return () => stopPolling(); }, [loadExisting, stopPolling]);

  // ── Run ─────────────────────────────────────────────────────────────────────
  const handleRun = async () => {
    setError(null);
    try {
      await hopTracerService.startV2(invId, {
        mode, maxHops, dayBuffer, maxNodes, fanOutThreshold,
        focusAddress: focusOverride || null,
      });
      setJobStatus({ status: 'queued', progress_pct: 0, nodes_found: 0, current_depth: 0, message: 'Queued…' });
      startPolling();
      if (focusOverride && onFocusAddressConsumed) onFocusAddressConsumed();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to start trace');
    }
  };

  // ── Cancel ──────────────────────────────────────────────────────────────────
  const handleCancel = async () => {
    try { await hopTracerService.cancelV2(invId); } catch {}
    stopPolling();
    setJobStatus(prev => prev ? { ...prev, status: 'cancelled' } : null);
  };

  const isRunning = ['queued', 'running'].includes(jobStatus?.status);

  // ── Crime window from seed data ──────────────────────────────────────────────
  const crimeWindow = React.useMemo(() => {
    if (!seedData?.transactions?.length) return null;
    const timestamps = seedData.transactions
      .map(tx => tx.timestamp ? new Date(tx.timestamp) : null)
      .filter(Boolean).map(d => d.getTime());
    if (!timestamps.length) return null;
    const first = new Date(Math.min(...timestamps));
    const last  = new Date(Math.max(...timestamps));
    return { first, last, close: new Date(last.getTime() + 30 * 24 * 3600 * 1000) };
  }, [seedData]);

  const stats    = result?.graph?.stats || {};
  const patterns = result?.patterns     || [];
  const nodes    = result?.graph?.nodes || [];

  return (
    <div className="hop-tracer-panel">

      {/* ── Focus address override banner ── */}
      {focusOverride && (
        <div className="ht-window-info" style={{ borderColor: 'rgba(0,217,255,0.4)' }}>
          <span>🎯 Trace root override:</span>
          <span><strong>{focusOverride}</strong></span>
          <button
            className="ht-cancel-btn"
            style={{ marginLeft: 'auto' }}
            onClick={() => { setFocusOverride(null); if (onFocusAddressConsumed) onFocusAddressConsumed(); }}
            title="Clear override and trace from the investigation's start address instead"
          >
            ✕ Use scammer wallet instead
          </button>
        </div>
      )}

      <div className="ht-controls-card">
        <h3>🔍 Hop Tracer v2 — Fund Flow Analysis</h3>
        <div className="ht-controls-grid ht-v2-grid">
          {/* Mode selector */}
          <div className="ht-field ht-field-mode">
            <label>Trace Mode</label>
            <div className="ht-mode-group">
              {['outbound', 'inbound', 'bidirectional'].map(m => (
                <button
                  key={m}
                  className={`ht-mode-btn ${mode === m ? 'active' : ''}`}
                  onClick={() => setMode(m)}
                  disabled={isRunning}
                >
                  {m === 'outbound' ? '→ Out' : m === 'inbound' ? '← In' : '⇄ Both'}
                </button>
              ))}
            </div>
          </div>

          <div className="ht-field">
            <label>Max Hops (1–4)</label>
            <input type="number" min={1} max={4} value={maxHops} disabled={isRunning}
              onChange={e => setMaxHops(Math.max(1, Math.min(4, +e.target.value)))} />
          </div>
          <div className="ht-field">
            <label>Day Buffer</label>
            <input type="number" min={0} max={30} value={dayBuffer} disabled={isRunning}
              onChange={e => setDayBuffer(Math.max(0, Math.min(30, +e.target.value)))} />
          </div>
          <div className="ht-field">
            <label>Max Nodes</label>
            <input type="number" min={10} max={200} value={maxNodes} disabled={isRunning}
              onChange={e => setMaxNodes(Math.max(10, Math.min(200, +e.target.value)))} />
          </div>
          <div className="ht-field">
            <label>Fan-out Threshold</label>
            <input type="number" min={2} max={50} value={fanOutThreshold} disabled={isRunning}
              onChange={e => setFanOutThreshold(Math.max(2, Math.min(50, +e.target.value)))} />
          </div>

          <div className="ht-btn-group">
            <button className="ht-run-btn" onClick={handleRun} disabled={isRunning}>
              ▶ Run Trace
            </button>
            {isRunning && (
              <button className="ht-cancel-btn" onClick={handleCancel}>
                ✕ Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Crime window ──────────────────────────────────────────────── */}
      {crimeWindow ? (
        <div className="ht-window-info">
          <span>📅 Crime window:</span>
          <span>First TX <strong>{fmtDate(crimeWindow.first.toISOString())}</strong></span>
          <span>Last TX <strong>{fmtDate(crimeWindow.last.toISOString())}</strong></span>
          <span>Closes <strong>{fmtDate(crimeWindow.close.toISOString())}</strong></span>
        </div>
      ) : (
        <div className="ht-window-info">
          ⚠️ No Seed TX data — 90-day fallback window will be used. Fetch Seed TXs first for best results.
        </div>
      )}

      {/* ── Progress bar ──────────────────────────────────────────────── */}
      {isRunning && jobStatus && (
        <ProgressBar
          pct={jobStatus.progress_pct || 0}
          message={jobStatus.message}
          nodes={jobStatus.nodes_found || 0}
          depth={jobStatus.current_depth || 0}
        />
      )}

      {/* ── Error ─────────────────────────────────────────────────────── */}
      {error && <div className="ht-error">{error}</div>}

      {/* ── Result ────────────────────────────────────────────────────── */}
      {result && !isRunning && (
        <>
          {/* Stats pills */}
          <div className="ht-stats-row">
            <div className="ht-stat-pill">Nodes <strong>{stats.node_count ?? result.total_nodes}</strong></div>
            <div className="ht-stat-pill">Edges <strong>{stats.edge_count ?? result.total_edges}</strong></div>
            <div className="ht-stat-pill">Terminal <strong>{stats.terminal_nodes ?? '—'}</strong></div>
            <div className="ht-stat-pill">Known hits <strong>{stats.known_addresses_hit ?? '—'}</strong></div>
            <div className="ht-stat-pill">Parked <strong>{stats.parked_wallets ?? '—'}</strong></div>
            <div className="ht-stat-pill">Peel chain <strong>{stats.peel_chain_nodes ?? '—'}</strong></div>
            {patterns.length > 0 && (
              <div className="ht-stat-pill ht-stat-alert">
                ⚠️ Patterns <strong>{patterns.length}</strong>
              </div>
            )}
            {result.mode && (
              <div className="ht-stat-pill">Mode <strong>{result.mode}</strong></div>
            )}
          </div>

          {/* Tab bar */}
          <div className="ht-tab-bar">
            {['graph', 'table', 'patterns', 'flow'].map(tab => (
              <button
                key={tab}
                className={`ht-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'graph'    && '🕸 Graph'}
                {tab === 'table'    && '📋 Hop Table'}
                {tab === 'patterns' && `⚠️ Patterns${patterns.length ? ` (${patterns.length})` : ''}`}
                {tab === 'flow'     && '💰 Value Flow'}
              </button>
            ))}
          </div>

          {/* Graph tab */}
          {activeTab === 'graph' && (
            <div className="ht-graph-card">
              <h3>
                Hop Graph
                <div className="ht-legend">
                  {[0, 1, 2, 3, 4].map(d => (
                    <div key={d} className="ht-legend-item">
                      <div className="ht-legend-dot" style={{ background: hopColor(d) }} />
                      {d === 0 ? 'Scammer' : `Hop ${d}`}
                    </div>
                  ))}
                  {result.mode !== 'outbound' && (
                    <div className="ht-legend-item">
                      <div className="ht-legend-dot" style={{ background: '#6b7280', border: '2px dashed #94a3b8' }} />
                      Inbound
                    </div>
                  )}
                </div>
              </h3>
              <div className="ht-graph-wrap">
                <Graph
                  data={result.graph}
                  startAddress={investigation?.start_address}
                  hiddenEdges={htHiddenEdges}
                  hiddenNodes={htHiddenNodes}
                  onViewStateChange={handleHtViewStateChange}
                />
              </div>
              <div className="ht-terminal-legend">
                {Object.entries(TERMINAL_LABELS).map(([key, meta]) => (
                  <div key={key} className="ht-terminal-item">
                    <div className="ht-terminal-dot" style={{ background: meta.color }} />
                    {meta.label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hop table tab */}
          {activeTab === 'table' && result.hop_summary?.length > 0 && (
            <div className="ht-summary-card">
              <h3>Hop Summary ({result.hop_summary.length} transfers)</h3>
              <table className="ht-summary-table">
                <thead>
                  <tr>
                    <th>Hop</th>
                    <th>Dir</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Amount</th>
                    <th>Date (UTC)</th>
                    <th>TX</th>
                  </tr>
                </thead>
                <tbody>
                  {result.hop_summary.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <span className="ht-hop-badge" style={{ background: hopColor(row.hop) }}>
                          {row.hop}
                        </span>
                      </td>
                      <td style={{ color: row.direction === 'in' ? '#a855f7' : '#00d9ff', fontSize: '0.8rem' }}>
                        {row.direction === 'in' ? '←' : '→'}
                      </td>
                      <td><span className="ht-addr" title={row.from}>{fmtAddr(row.from)}</span></td>
                      <td>
                        <span className="ht-addr" title={row.to}>{fmtAddr(row.to)}</span>
                        {row.known_name && <span className="ht-known-badge">{row.known_name}</span>}
                      </td>
                      <td>{(+row.value).toFixed(2)} {row.token}</td>
                      <td style={{ fontSize: '0.75rem', color: '#64748b' }}>{fmtDate(row.timestamp)}</td>
                      <td>
                        {row.tx_hash
                          ? <span className="ht-addr" title={row.tx_hash}>{row.tx_hash.slice(0, 12)}…</span>
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'table' && !result.hop_summary?.length && (
            <div className="ht-empty"><div className="ht-empty-icon">📋</div><div>No hop summary data.</div></div>
          )}

          {/* Patterns tab */}
          {activeTab === 'patterns' && (
            patterns.length > 0
              ? <PatternAlerts patterns={patterns} />
              : <div className="ht-empty"><div className="ht-empty-icon">✅</div><div>No suspicious patterns detected.</div></div>
          )}

          {/* Value flow tab */}
          {activeTab === 'flow' && (
            nodes.length > 0
              ? <ValueFlowPanel nodes={nodes} />
              : <div className="ht-empty"><div className="ht-empty-icon">💰</div><div>No value flow data.</div></div>
          )}
        </>
      )}

      {/* ── Initial empty state ───────────────────────────────────────── */}
      {!result && !isRunning && !error && (
        <div className="ht-empty">
          <div className="ht-empty-icon">🕸️</div>
          <div>No trace run yet.</div>
          <div style={{ fontSize: '0.8rem', color: '#475569' }}>
            Choose a mode and click <strong>Run Trace</strong> to follow funds from the scammer wallet.
          </div>
        </div>
      )}
    </div>
  );
}
